import { useEffect, useRef } from "react";
import type { Document } from "@scenix/core";
import { Markdown } from "./Markdown";

interface TreatmentDialogProps {
  treatment: Document | undefined;
  loading: boolean;
  mayEdit: boolean;
  editing: boolean;
  title: string;
  body: string;
  onTitle: (value: string) => void;
  onBody: (value: string) => void;
  onEdit: () => void;
  onCancel: () => void;
  onSave: () => void;
  saving: boolean;
  error: string | null;
  onClose: () => void;
}

/**
 * The treatment, opened rather than printed (ADR 000064).
 *
 * It is the most important document on a project and it is read deliberately,
 * not continuously: somebody opens it to remember what was agreed, not while
 * dragging a segment. Printed in full it was the largest thing on the page
 * whatever its length, and a long one pushed the chain off the screen.
 *
 * A real `<dialog>` rather than a div pretending to be one, so Escape closes
 * it, focus is trapped, and the rest of the page is inert without any of that
 * being written here.
 */
export function TreatmentDialog({
  treatment,
  loading,
  mayEdit,
  editing,
  title,
  body,
  onTitle,
  onBody,
  onEdit,
  onCancel,
  onSave,
  saving,
  error,
  onClose,
}: TreatmentDialogProps) {
  const dialog = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const element = dialog.current;
    if (!element || element.open) return;
    element.showModal();
  }, []);

  return (
    <dialog
      ref={dialog}
      className="treatment-dialog"
      aria-label="Treatment"
      onClose={onClose}
      // Clicking the backdrop is clicking the dialog itself, since the content
      // sits in a child. Anything inside stops here.
      onClick={(event) => {
        if (event.target === dialog.current) dialog.current?.close();
      }}
    >
      <div className="treatment-dialog-body">
        <header className="section-bar">
          <h2>Treatment</h2>
          <div className="row">
            {mayEdit && treatment && !editing && (
              <button type="button" className="ghost" onClick={onEdit}>
                Edit
              </button>
            )}
            <button type="button" className="ghost" onClick={() => dialog.current?.close()}>
              Close
            </button>
          </div>
        </header>

        {error && (
          <p role="alert" className="error">
            {error}
          </p>
        )}

        {editing && treatment ? (
          <form
            className="document-edit"
            onSubmit={(event) => {
              event.preventDefault();
              onSave();
            }}
          >
            <label htmlFor="treatment-title">Title</label>
            <input
              id="treatment-title"
              value={title}
              maxLength={300}
              onChange={(event) => onTitle(event.target.value)}
            />
            <label htmlFor="treatment-body">Body</label>
            <textarea
              id="treatment-body"
              className="document-body-input"
              value={body}
              rows={18}
              onChange={(event) => onBody(event.target.value)}
            />
            <div className="compose-row">
              <button type="submit" disabled={!title.trim() || saving}>
                {saving ? "Saving…" : "Save"}
              </button>
              <button type="button" className="ghost" onClick={onCancel}>
                Cancel
              </button>
            </div>
          </form>
        ) : treatment ? (
          <article className="document">
            <h3>{treatment.title}</h3>
            <Markdown>{treatment.body}</Markdown>
            <p className="muted">Revision {treatment.revision}</p>
          </article>
        ) : (
          <p className="muted">{loading ? "Loading…" : "No treatment on this project."}</p>
        )}
      </div>
    </dialog>
  );
}
